// Patient identity, administrative fields and questionnaire answers that must
// never leave the device in the data Sentry attaches to events. This is a
// denylist: a new field on the Prisma `Patient` model (the shape stored in
// `consultation.patient`) leaks until it is added here or allowed in
// PATIENT_KEY_POLICY (sentryState.spec.ts), whose type forces the choice.
export const SENSITIVE_PATIENT_KEYS = new Set([
  'firstName',
  'lastName',
  'dateOfBirth',
  'patientId',
  'sourcePatientId',
  'socialSecurityNumber',
  'birthName',
  'birthPlace',
  'birthPlaceInseeCode',
  'addressStreet',
  'addressPostalCode',
  'addressCity',
  'addressCountry',
  'profession',
  'phone',
  'email',
  'userResponses',
  'report',
]);

// Keys only sensitive under a given parent: `id` is generic but identifies the
// patient on patient objects, and `kb.responses` (KB definitions) must stay
// readable while `graph.values` holds the patient's answers.
const SENSITIVE_KEYS_BY_PARENT: Record<string, Set<string>> = {
  patient: new Set(['id']),
  patients: new Set(['id']),
  graph: new Set(['values']),
  questionnaire: new Set(['checklistItems']),
};

// The professional's own identity stays readable for debugging.
const PROFESSIONAL_KEYS = new Set(['professional', 'professionalByDefault']);
const PROFESSIONAL_IDENTITY_KEYS = new Set([
  'firstName',
  'lastName',
  'email',
  'phone',
]);

const REDACTED = '[REDACTED]';
const MAX_DEPTH = 12;

const isSensitive = (key: string, parentKey: string | undefined) => {
  if (parentKey === undefined) return SENSITIVE_PATIENT_KEYS.has(key);
  if (PROFESSIONAL_KEYS.has(parentKey) && PROFESSIONAL_IDENTITY_KEYS.has(key)) {
    return false;
  }
  return (
    SENSITIVE_PATIENT_KEYS.has(key) ||
    (SENSITIVE_KEYS_BY_PARENT[parentKey]?.has(key) ?? false)
  );
};

/** Returns a copy of `value` with every sensitive patient key redacted. */
export const scrubSentryState = (
  value: unknown,
  depth = 0,
  parentKey?: string,
): unknown => {
  if (depth > MAX_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => scrubSentryState(item, depth + 1, parentKey));
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      isSensitive(key, parentKey) && item != null
        ? REDACTED
        : scrubSentryState(item, depth + 1, key),
    ]),
  );
};

/**
 * Redacts the same keys in the data an event carries directly, e.g.
 * `Sentry.captureMessage(…, { extra: { consultation } })` or
 * `Sentry.addBreadcrumb({ data })`, which the Redux transformers never see.
 */
export const scrubSentryEvent = <
  T extends {
    extra?: Record<string, unknown>;
    contexts?: Record<string, unknown>;
    breadcrumbs?: { data?: Record<string, unknown> }[];
  },
>(
  event: T,
): T => ({
  ...event,
  ...(event.extra && { extra: scrubSentryState(event.extra) as T['extra'] }),
  ...(event.contexts && {
    contexts: scrubSentryState(event.contexts) as T['contexts'],
  }),
  ...(event.breadcrumbs && {
    breadcrumbs: event.breadcrumbs.map((breadcrumb) =>
      breadcrumb.data
        ? { ...breadcrumb, data: scrubSentryState(breadcrumb.data) }
        : breadcrumb,
    ) as T['breadcrumbs'],
  }),
});

/**
 * Keeps only the action type in the Redux breadcrumbs Sentry records on every
 * dispatch: payloads carry the saved patient (NIR, address…), and the
 * enhancer's default transformer would send them as-is with the next error.
 */
export const scrubSentryAction = (action: { type: string }) => ({
  type: action.type,
});
